import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('dmarc-alignment');

/**
 * The diagnostic page of the cluster. Highest-intent query in the set ("spf passes but dmarc
 * fails") and the one thing every DMARC report surfaces that nobody can interpret.
 */
export default function DmarcAlignmentPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Alignment is the requirement that a passing SPF or DKIM check belongs to the same domain a reader sees in the From: line. SPF checks the envelope sender and DKIM checks its own signing domain — neither is the visible address. When those differ, authentication passes and DMARC still fails.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Three domains in one message">
        <p>
          Every message carries at least three domain names, and readers only ever see one of them.
          Alignment is the rule connecting the other two back to it.
        </p>
        <LearnTable
          head={['Domain', 'Where it lives', 'Who reads it', 'Checked by']}
          rows={[
            [
              'Envelope sender',
              <span key="a">The SMTP <Tok>MAIL FROM</Tok> / <Tok>Return-Path</Tok></span>,
              'Mail servers only',
              'SPF',
            ],
            [
              'Signing domain',
              <span key="b">The <Tok>d=</Tok> tag in the DKIM signature</span>,
              'Mail servers only',
              'DKIM',
            ],
            [
              'Header From',
              <span key="c">The <Tok>From:</Tok> header</span>,
              <Lead key="d">The human</Lead>,
              'Neither — until DMARC',
            ],
          ]}
        />
        <p>
          That gap is the entire reason DMARC exists. Without it, a spoofer passes SPF for a domain
          they genuinely control while putting your domain in the visible <Tok>From:</Tok>. Both
          checks pass; the message is a forgery.
        </p>
      </LearnSection>

      <LearnSection title="Why SPF passes and DMARC still fails">
        <p>The single most common case, and it is not a misconfiguration:</p>
        <LearnCode>
          {`Return-Path: bounces@mail.your-esp.com     ← SPF checks THIS domain      ✓ pass
From:        hello@yourdomain.com            ← the reader sees THIS
                                               different domain → SPF NOT ALIGNED ✗`}
        </LearnCode>
        <p>
          Your ESP sets its own bounce domain so it can process bounces. SPF passes cleanly —
          for <em>their</em> domain. DMARC compares that to your <Tok>From:</Tok>, finds no
          relationship, and discards the SPF pass as unaligned.
        </p>
        <p>
          Two fixes. Configure a <Lead>custom return path</Lead> at your ESP so the envelope
          becomes a subdomain of yours — most support this, often called custom bounce domain or
          custom MAIL FROM. Or rely on <Lead>DKIM alignment instead</Lead>, which you almost
          certainly already have, since the ESP signs with <Tok>d=yourdomain.com</Tok>. DMARC needs
          only one of the two to align.
        </p>
      </LearnSection>

      <LearnSection title="Relaxed and strict">
        <p>
          Both modes exist for both checks, and relaxed is the default for both. The difference is
          whether subdomains count.
        </p>
        <LearnTable
          head={['From:', 'Authenticated domain', 'Relaxed (default)', 'Strict']}
          rows={[
            ['hello@yourdomain.com', 'yourdomain.com', <Lead key="a">Aligned</Lead>, <Lead key="b">Aligned</Lead>],
            ['hello@yourdomain.com', 'mail.yourdomain.com', <Lead key="c">Aligned</Lead>, 'Not aligned'],
            ['hello@mail.yourdomain.com', 'yourdomain.com', <Lead key="d">Aligned</Lead>, 'Not aligned'],
            ['hello@yourdomain.com', 'yourdomain.co', 'Not aligned', 'Not aligned'],
            ['hello@yourdomain.com', 'your-esp.com', 'Not aligned', 'Not aligned'],
          ]}
        />
        <p>
          Set strict mode with <Tok>adkim=s</Tok> and <Tok>aspf=s</Tok>. Almost nobody should:
          relaxed already blocks every cross-organisation forgery, and strict breaks the moment
          any legitimate sender uses a subdomain. Reach for it only if you have a specific reason
          to distrust your own subdomains.
        </p>
      </LearnSection>

      <LearnSection title="Forwarding: the case where alignment saves you">
        <p>
          A user sets up forwarding from an old address to a new one. The forwarder re-sends your
          message from its own IP.
        </p>
        <LearnCode>
          {`original:   your server ──────────────────► recipient        SPF ✓ aligned  DKIM ✓ aligned
forwarded:  your server ──► forwarder ──────► recipient        SPF ✗ (wrong IP)  DKIM ✓ aligned`}
        </LearnCode>
        <p>
          SPF cannot survive this — the connecting IP is the forwarder’s and your record does not
          list it. Nothing is broken. But DMARC needs only one aligned pass, and the DKIM signature
          travelled inside the message untouched, so the message still passes DMARC.
        </p>
        <p>
          This is the concrete reason to have both records right rather than one. A domain
          authenticating on SPF alone loses DMARC on every forwarded message. A domain with{' '}
          <Link to="/learn/dkim" className="text-accent underline underline-offset-4">
            aligned DKIM
          </Link>{' '}
          does not.
        </p>
      </LearnSection>

      <LearnSection title="Diagnosing your own reports">
        <LearnSubsection title="SPF pass, DKIM pass, DMARC fail">
          <p>
            Neither passed <em>for your domain</em>. Check the ESP’s return path and the{' '}
            <Tok>d=</Tok> value it signs with — if <Tok>d=</Tok> is the ESP’s own domain, ask them
            to sign with yours. That is a settings change at their end, not a DNS change at yours.
          </p>
        </LearnSubsection>
        <LearnSubsection title="One source fails, everything else passes">
          <p>
            A single sending service is misconfigured. Almost always its DKIM key was rotated at
            the provider and the DNS record was never updated, or its{' '}
            <Tok>include:</Tok> was dropped from your SPF record.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Everything fails, from every source, at once">
          <p>
            Not alignment. Look at SPF first — this is the signature of{' '}
            <Link to="/learn/spf-too-many-dns-lookups" className="text-accent underline underline-offset-4">
              crossing the 10-lookup limit
            </Link>
            , where a permerror voids SPF everywhere simultaneously.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Failures from sources you do not recognise">
          <p>
            Either genuine spoofing, or a forgotten internal sender. Resolve the IP before assuming
            the first — the invoicing system nobody documented looks exactly like an attacker in an
            aggregate report.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="The rule, compressed">
        <LearnList>
          <li>DMARC passes if <Lead>SPF aligns and passes</Lead>, or <Lead>DKIM aligns and passes</Lead>.</li>
          <li>One is enough. Both is better, because they fail in different situations.</li>
          <li>Aligns means <Lead>same organisational domain as the visible From:</Lead> — subdomains count under relaxed.</li>
          <li>An unaligned pass is worth exactly as much as a failure. This is the part people miss.</li>
        </LearnList>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'Why does DMARC fail when SPF passes?',
    a: 'Because SPF checked a different domain from the one your reader sees. Most ESPs set their own bounce domain as the envelope sender, so SPF passes for the ESP while your From: header shows your domain. DMARC compares the two, finds no relationship, and treats the SPF pass as unaligned.',
  },
  {
    q: 'What is the difference between relaxed and strict alignment?',
    a: 'Relaxed, the default, counts subdomains as aligned — mail.yourdomain.com aligns with yourdomain.com. Strict requires an exact match. Relaxed already stops every cross-organisation forgery, and strict breaks as soon as any legitimate sender uses a subdomain.',
  },
  {
    q: 'Do I need both SPF and DKIM to align?',
    a: 'No, one is enough for DMARC to pass. Having both is still worth it because they fail in different circumstances: SPF breaks on forwarding, DKIM breaks when a mailing list rewrites the body. Cover both and one survives each case.',
  },
  {
    q: 'How do I make SPF align?',
    a: 'Configure a custom return path at your sending provider so the envelope sender becomes a subdomain of your own domain. Most providers support this under a name like custom bounce domain or custom MAIL FROM. It is a setting at their end plus a DNS record at yours.',
  },
  {
    q: 'Does forwarding break DMARC?',
    a: 'It breaks SPF, because the forwarder connects from its own IP. It does not break DKIM, because the signature travels inside the message. So a domain with aligned DKIM still passes DMARC on forwarded mail, and a domain relying on SPF alone does not.',
  },
  {
    q: 'What does an unaligned pass count for?',
    a: 'Nothing, as far as DMARC is concerned. A passing SPF or DKIM check on a domain unrelated to your From: header is treated exactly like a failure. That is precisely the loophole DMARC was designed to close.',
  },
];
